import React, { useEffect, useState } from 'react';
import { Building2, ArrowRight, Loader2 } from 'lucide-react';
import { BUSINESS } from '@/config/business';
import { listCompanies } from '@/lib/business';
import { Mark } from '@/components/business/parts';

// "My companies", next to My sites (Business plan §2.1): every company on the
// account, where it is in its life, one click from its page.

function stateOf(c) {
  if (c.status === 'naming') return { label: c.mode === 'grow' ? 'Ready to build' : 'Pick a name', tone: 'text-zinc-400' };
  if (c.status === 'building') return { label: 'Being built', tone: 'text-orange-400' };
  if (c.status === 'failed') return { label: 'Build stopped', tone: 'text-red-400' };
  if (c.registration) return { label: `${c.registration.label} · ${c.registration.ref}`, tone: c.registration.status === 'query' ? 'text-amber-400' : 'text-emerald-400' };
  return { label: c.mode === 'grow' ? 'Ready' : 'Preview ready', tone: 'text-emerald-400' };
}

export default function MyCompanies({ navigate, limit = 0, title = 'My companies', onStart = null, hideWhenEmpty = false, onEmpty = null }) {
  const [companies, setCompanies] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    listCompanies()
      .then(d => { if (!cancelled) setCompanies(d.companies || []); })
      .catch(err => { if (!cancelled) { setError(err.message); setCompanies([]); } });
    return () => { cancelled = true; };
  }, []);

  // Lets a parent drop the frame it drew around this when there is nothing in it.
  useEffect(() => {
    if (companies && !companies.length && hideWhenEmpty) onEmpty?.();
  }, [companies, hideWhenEmpty, onEmpty]);

  if (companies === null) {
    return <div className="flex items-center gap-2 text-sm text-zinc-500 p-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your companies…</div>;
  }
  if (!companies.length && hideWhenEmpty) return null;
  const shown = limit ? companies.slice(0, limit) : companies;

  return (
    <section aria-label={title}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-orange-400" />{title}</h2>
        {limit > 0 && companies.length > limit && (
          <button onClick={() => navigate('/build?view=dashboard&section=companies')} className="text-xs font-semibold text-orange-400 hover:text-orange-300">View all</button>
        )}
      </div>
      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
      {!companies.length ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-6 text-center">
          <p className="text-white font-semibold">No companies yet</p>
          <p className="text-sm text-zinc-400 mt-1">Describe one in a sentence. The preview is free: you only pay when you register.</p>
          {onStart && (
            <button onClick={onStart} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-ink px-4 py-2 text-sm font-bold">
              Start a company <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.map(c => {
            const s = stateOf(c);
            return (
              <button key={c.id} onClick={() => navigate(`/company/${c.id}`)}
                className="text-left rounded-2xl border border-zinc-800 bg-zinc-950/40 hover:border-zinc-700 p-4 flex items-center gap-3 transition-colors">
                <Mark svg={c.iconSvg} name={c.name || '?'} className="w-11 h-11" />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-white truncate">{c.legalName || 'Unnamed company'}</span>
                  <span className="block text-[11px] text-zinc-500 truncate">{BUSINESS.countries[c.country]?.short}</span>
                  <span className={`block text-xs font-semibold mt-1 truncate ${s.tone}`}>{s.label}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-zinc-600 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
